import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { forkJoin, Observable, of } from 'rxjs';
import { FeeCode } from 'src/app/enums/fee.code';
import { Role } from 'src/app/enums/role';
import { Agent } from 'src/app/model/agent';
import { City } from 'src/app/model/city';
import { Fees } from 'src/app/model/fees';
import { GenericResponse } from 'src/app/model/generic.response';
import { Merchant } from 'src/app/model/merchant';
import { Personal } from 'src/app/model/personal';
import { User } from 'src/app/model/user';
import { UserAddress } from 'src/app/model/user.address';
import { AlertService } from 'src/app/services/alert.service';
import { CommonService } from 'src/app/services/common.service';
import { CommonUtil } from 'src/app/services/common.util';
import { CompanyService } from 'src/app/services/company.service';
import { LoginService } from 'src/app/services/login.service';
import { StorageService } from 'src/app/services/storage.service';
import { TransactionService } from 'src/app/services/transaction.service';
import { MessageConstant } from 'src/app/settings/message.constant';

interface ProvinceOption {
    id: string | number;
    province: string;
}

@Component({
    selector: 'app-user-profile-settings',
    templateUrl: './user-profile-settings.component.html',
    styleUrls: ['./user-profile-settings.component.css'],
})
export class UserProfileSettingsComponent implements OnInit {
    private readonly destroyRef = inject(DestroyRef);

    TIMEOUT_AFTER_ALERT = 1500;
    iconInfoMsg = MessageConstant.SUPPLIER_DIRECT_ICON_MSG;
    feeCode = FeeCode;

    display = false;
    displaySuccess = false;
    isVisible = false;
    isSupplierDirect = false;
    user: User = new User();
    eMessage = '';
    sMessage = '';
    id = '';
    form: FormGroup;
    editPersonal = false;
    editBusiness = false;
    editAgent = false;
    statusDropdown: string[] = [];
    province: ProvinceOption[] = [];
    provinceDropdown: string[] = [];
    cityDropdown: string[] = [];
    cityList: City[] = [];
    accountTypeDropdown: string[] = [];
    invalidForm = false;
    businessFeeList: Array<Fees> = [];
    agentFeeList: Array<Fees> = [];
    feeLabel = this.commonUtil.getFeeLabels();
    alertOptions = {
        autoClose: true,
        keepAfterRouteChange: true,
    };
    userLinkedToSafe = false;

    constructor(
        private commonService: CommonService,
        private route: ActivatedRoute,
        private formBuilder: FormBuilder,
        private transactionService: TransactionService,
        private storageService: StorageService,
        public companyService: CompanyService,
        public loginService: LoginService,
        public commonUtil: CommonUtil,
        private alertService: AlertService,
    ) {
        this.form = this.buildForm();
    }

    ngOnInit(): void {
        this.route.paramMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
            next: (params) => {
                this.id = params.get('id') ?? '';
                this.getData(false);
            },
        });

        this.commonService.getUserStatus().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
            next: (res) => {
                this.statusDropdown = res.data;
            },
        });

        this.commonService.getProvinces().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
            next: (res) => {
                this.province = res;
                this.provinceDropdown = this.province.map((val) => val.province);
            },
            error: () => {
            },
        });

        this.form
            .get('agentAddress.provinceName')
            ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((newVal: string) => {
                if (newVal) {
                    this.form.get('agentAddress.cityName')?.setValue('');
                    this.form.get('agentAddress.cityId')?.setValue('');

                    this.commonService
                        .getCities(this.getProvinceId(newVal))
                        .pipe(takeUntilDestroyed(this.destroyRef))
                        .subscribe({
                            next: (res) => {
                                this.cityList = res.responseBody.data || [];
                                this.createCityDropDown();
                            },
                        });
                } else {
                    this.cityList = [];
                    this.cityDropdown = [];
                    this.form.get('agentAddress.cityName')?.setValue('');
                    this.form.get('agentAddress.cityId')?.setValue('');
                }
            });

        this.form
            .get('agentAddress.cityName')
            ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((newVal: string) => {
                this.form.get('agentAddress.cityId')?.setValue(this.getCityId(newVal), {
                    emitEvent: false,
                });
            });
    }

    private buildForm(): FormGroup {
        return this.formBuilder.group({
            personalStatus: [''],
            businessStatus: [''],
            agentStatus: [''],
            visibleInPayList: [false],
            supplierDirect: [false],
            agentRole: [''],
            agentOperationalLocation: [''],
            agentAddress: this.formBuilder.group({
                type: ['home'],
                addressLineOne: [''],
                addressLineTwo: [''],
                buildingDetails: [''],
                provinceName: [''],
                cityName: [''],
                cityId: [''],
                suburb: [''],
                postalCode: [''],
            }),
        });
    }

    getData(reload: boolean): void {
        window.scroll(0, 0);
        this.editPersonal = false;
        this.editBusiness = false;
        this.editAgent = false;

        const cachedUser = this.storageService.getProfileUser();

        if (!reload && cachedUser?.id === this.id) {
            this.user = cachedUser;
            this.initializeUser();
            this.populateForm();
            this.getFeeList(Role.business);
            this.getFeeList(Role.agent);
        } else {
            this.commonService.getUserProfile(this.id).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
                next: (res) => {
                    this.user = res.data;
                    this.initializeUser();
                    this.storageService.setProfileUser(this.user);
                    this.populateForm();
                    this.getFeeList(Role.business);
                    this.getFeeList(Role.agent);
                },
                error: () => {
                },
            });
        }
    }

    getFeeList(role: string): void {
        let userRoleId = '';
        if (role === Role.business) {
            userRoleId = this.user.userBusinessProfileModel.userRoleId;
            this.businessFeeList = [];
        } else {
            userRoleId = this.user.userAgentProfileModel.userRoleId;
            this.agentFeeList = [];
        }

        if (userRoleId) {
            this.transactionService
                .getCustomFee(userRoleId, role)
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe({
                    next: (res) => {
                        if (res.data.length > 0) {
                            if (role === Role.business) {
                                this.businessFeeList = [];
                            } else {
                                this.agentFeeList = [];
                            }
                            this.setFee(res.data || []);
                        }
                    },
                    error: (err) => console.log(err),
                });
        }
    }

    setFee(feeList: Fees[]): void {
        feeList.forEach((fee) => {
            fee.percentage = fee.percentage ?? 0;
            fee.amount = fee.amount ?? 0;
            fee.useAmount = fee.useAmount ?? false;
            fee.percentageValue = fee.percentageValue ?? fee.percentage ?? 0;
            fee.amountValue = fee.amountValue ?? fee.amount ?? 0;
            fee.useAmountValue = fee.useAmountValue ?? fee.useAmount ?? false;

            if (fee.code.includes(Role.business)) {
                this.businessFeeList.push(fee);
            } else if (fee.code.includes(Role.agent)) {
                this.agentFeeList.push(fee);
            }
        });
    }

    hasSafe(): void {
        this.userLinkedToSafe = Boolean(
            this.user.userAgentProfileModel?.roleId && this.user.userAgentProfileModel?.hasSafe,
        );
    }

    initializeUser(): void {
        if (!this.user.userPersonalProfileModel) {
            this.user.userPersonalProfileModel = new Personal();
        }
        if (this.user.userBusinessProfileModel) {
            this.isVisible = this.user.userBusinessProfileModel.visibleInPayList ?? false;
            this.isSupplierDirect = this.user.userBusinessProfileModel.supplierDirect ?? false;
        } else {
            this.user.userBusinessProfileModel = new Merchant();
        }
        if (!this.user.userAgentProfileModel) {
            this.user.userAgentProfileModel = new Agent();
        }
        if (!this.user.userAgentProfileModel.address) {
            this.user.userAgentProfileModel.address = new UserAddress();
        }
        if (this.user.userAgentProfileModel.address.provinceName) {
            this.loadCitiesForProvince(this.user.userAgentProfileModel.address.provinceName);
        }

        this.user.lastLogin = this.user.userPersonalProfileModel.lastLogin;
    }

    private loadCitiesForProvince(provinceName: string): void {
        const provinceId = this.getProvinceId(provinceName);
        if (!provinceId) {
            this.cityList = [];
            this.cityDropdown = [];
            return;
        }

        this.commonService.getCities(provinceId).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
            next: (res) => {
                this.cityList = res.responseBody.data || [];
                this.createCityDropDown();
            },
        });
    }

    closeModel(_: Event | boolean): void {
        this.display = false;
        this.displaySuccess = false;
    }

    changeStatus(): void {
        this.isVisible = !this.isVisible;
        this.form.get('visibleInPayList')?.setValue(this.isVisible);
    }

    // Change status of supplier direct
    changeStatusSD(): void {
        this.isSupplierDirect = !this.isSupplierDirect;
        this.form.get('supplierDirect')?.setValue(this.isSupplierDirect);
    }

    getProvinceId(name: string): string {
        const provinceId = this.province.find((item) => item.province === name)?.id;
        if (provinceId === undefined || provinceId === null) {
            return '';
        }
        return String(provinceId);
    }

    getCityId(val: string): number | string {
        return this.cityList.find((city) => city.city === val)?.id ?? '';
    }

    createCityDropDown(): void {
        this.cityDropdown = this.cityList.map((city) => city.city);
    }

    populateForm(): void {
        const businessProfile = this.user.userBusinessProfileModel;
        const agentProfile = this.user.userAgentProfileModel;
        const address = agentProfile.address;

        this.isVisible = businessProfile.visibleInPayList ?? false;
        this.isSupplierDirect = businessProfile.supplierDirect ?? false;

        this.form.patchValue({
            personalStatus: this.user.status ?? '',
            businessStatus: businessProfile.businessStatus ?? '',
            visibleInPayList: this.isVisible,
            supplierDirect: this.isSupplierDirect,
            agentStatus: agentProfile.agentAccountStatus ?? '',
            agentRole: agentProfile.agentRole ?? '',
            agentOperationalLocation: agentProfile.operationalLocation ?? '',
            agentAddress: {
                type: address.type || 'home',
                addressLineOne: address.addressLineOne ?? '',
                addressLineTwo: address.addressLineTwo ?? '',
                buildingDetails: address.buildingDetails ?? '',
                provinceName: address.provinceName ?? '',
                cityName: address.cityName ?? '',
                cityId: address.cityId ?? '',
                suburb: address.suburb ?? '',
                postalCode: address.postalCode ?? '',
            },
        });
    }

    changeView(_: Event | string, type: string): void {
        switch (type) {
            case 'personal':
                this.editPersonal = !this.editPersonal;
                break;
            case 'business':
                this.hasSafe();
                this.editBusiness = !this.editBusiness;
                this.resetVal(this.businessFeeList);
                if (!this.editBusiness) {
                    this.populateForm();
                }
                break;
            case 'agent':
                this.editAgent = !this.editAgent;
                this.resetVal(this.agentFeeList);
                if (!this.editAgent) {
                    this.populateForm();
                }
                break;
            default:
                break;
        }
    }

    resetVal(feeList: Fees[]): void {
        feeList.forEach((fee) => {
            fee.percentage = fee.percentage ?? 0;
            fee.amount = fee.amount ?? 0;
            fee.useAmount = fee.useAmount ?? false;
            fee.percentageValue = fee.percentage ?? 0;
            fee.amountValue = fee.amount ?? 0;
            fee.useAmountValue = fee.useAmount ?? false;
        });
    }

    submit(type: string): void {
        const settingsRequest = this.getSettingsRequest(type);
        const feeRequest = this.getFeeUpdateRequest(type);

        forkJoin({
            settings: settingsRequest,
            fees: feeRequest,
        })
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: ({ settings, fees }) => {
                    if (fees && !fees.success) {
                        this.display = true;
                        this.eMessage = fees.status.message;
                        return;
                    }

                    if (fees?.success) {
                        if (type === 'business') {
                            this.businessFeeList = [];
                        } else if (type === 'agent') {
                            this.agentFeeList = [];
                        }
                        this.setFee(fees.data || []);
                    }

                    this.onSettingsUpdate(settings);
                },
                error: () => {
                },
            });
    }

    private getSettingsRequest(type: string): Observable<GenericResponse<unknown>> {
        const roleId = this.getRoleIdForUpdate(type);
        return this.commonService.changeUserProfileSettings(
            this.user.id,
            roleId,
            this.form.getRawValue(),
        );
    }

    private getRoleIdForUpdate(type: string): string {
        switch (type) {
            case 'personal':
                return this.user.userPersonalProfileModel.roleId;
            case 'business':
                return this.user.userBusinessProfileModel.roleId;
            case 'agent':
                return this.user.userAgentProfileModel.roleId;
            default:
                return '';
        }
    }

    private getFeeUpdateRequest(type: string): Observable<GenericResponse<Fees[]> | null> {
        if (type === 'personal') {
            return of(null);
        }

        return this.transactionService.updateCustomFees(this.buildFeePayload(type));
    }

    private buildFeePayload(type: string): Array<Record<string, string | number | boolean>> {
        const feeList = type === 'business' ? this.businessFeeList : this.agentFeeList;
        const userRoleId =
            type === 'business'
                ? this.user.userBusinessProfileModel.userRoleId
                : this.user.userAgentProfileModel.userRoleId;

        return feeList.map((fee) => ({
            code: fee.code,
            percentage: fee.percentageValue,
            amount: fee.amountValue,
            useAmount: fee.useAmountValue,
            userRoleId,
        }));
    }

    resendLink(): void {
        this.loginService
            .resendCreatePasswordLink(this.user.code)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (response) => {
                    if (response.success) {
                        this.sMessage = MessageConstant.RESEND_CREATE_PASSWORD_LINK_SUCCESS;
                        this.displaySuccess = true;
                    } else {
                        this.eMessage = 'Unable to resend e-mail, Please try again later';
                        this.display = true;
                    }
                },
                error: () => {
                    this.eMessage = 'Service unavailable, Please try again later.';
                    this.display = true;
                },
            });
    }

    onSettingsUpdate(response: GenericResponse<unknown>): void {
        if (response.success) {
            this.getData(true);

            setTimeout(() => {
                if (true) {
                    this.alertService.success(
                        MessageConstant.USER_ADMIN_SETTINGS_UPDATE_SUCCESS,
                        this.alertOptions,
                    );
                }
            }, this.TIMEOUT_AFTER_ALERT);
        } else if (response.status) {
            this.alertService.error(response.status.message, this.alertOptions);
            setTimeout(() => { }, this.TIMEOUT_AFTER_ALERT);
        } else {
            this.alertService.error(
                'Something went wrong with your update. Try again',
                this.alertOptions,
            );
            setTimeout(() => { }, this.TIMEOUT_AFTER_ALERT);
        }
    }
}
